import {Container, HotjarSuppressor, Icon, Toast as toast} from "components";
import TypoGraph from "components/typograph";
import React, {createRef} from "react";
import Dropzone from "react-dropzone";
import {useFormContext, Controller} from "react-hook-form";
import colors from "theme/colors";
import styles from "./dropzone.module.scss";
import {ErrorMessage} from "@hookform/error-message";
import Resizer from "react-image-file-resizer";

interface SignatureDropzoneControllerProps {
    name: string;
    label: string;
    defaultValue?: File[];
    required?: boolean;
}

const SignatureDropzoneController: React.FC<SignatureDropzoneControllerProps> = ({
                                                                                     name,
                                                                                     label,
                                                                                     required = false,
                                                                                 }) => {
    const {control, setValue, getValues} = useFormContext();
    const dropzoneRef: any = createRef();
    const isRequired = required ? "Field is Required" : undefined;
    const previewName = name + "Preview";
    const maxWidth = 2560;
    const maxHeight = 1440;

    const resizeFile = async (file: File) =>
        new Promise<string>((resolve) => {
            // if file size is higher than 2MB, the resize quality will be 50%, otherwise, keep it at 100% quality
            const quality = file.size > 2 * 1024 * 1024 ? 50 : 100;
            Resizer.imageFileResizer(
                file,
                maxWidth,
                maxHeight,
                "PNG",  // Use PNG to preserve transparency
                quality,
                0,
                (uri) => resolve(uri.toString()),
                "base64"
            );
        });

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={undefined}
            rules={{
                required: isRequired,
            }}
            render={({
                         field: {onChange, value, onBlur},
                         formState: {errors},
                     }) => {
                return (
                    <Container>
                        {getValues(previewName) && (
                            <HotjarSuppressor>
                                <Icon
                                    name="signature"
                                    type="images"
                                    width={400}
                                    height={200}
                                    url={getValues(previewName)}
                                    imageId={"signatureImage"}
                                    boxClassName={styles.checkImage}
                                    objectFit="contain"
                                />
                            </HotjarSuppressor>
                        )}
                        <Dropzone
                            ref={dropzoneRef}
                            accept={{"image/*": []}}
                            multiple={false}
                            onDrop={async (file: any) => {
                                try {
                                    const image = await resizeFile(file[0]);
                                    const base64Data = image?.toString()?.replace("data:image/png;base64,", "");

                                    // Debug logging
                                    console.log("=== SignatureDropzone Debug ===");
                                    console.log("Raw image from resizeFile:", image?.substring(0, 100));
                                    console.log("After replace:", base64Data?.substring(0, 50));
                                    console.log("Base64Data length:", base64Data?.length);
                                    console.log("Starts with iVBOR (PNG):", base64Data?.startsWith('iVBOR'));
                                    console.log("==============================");

                                    setValue(name, base64Data);
                                    setValue(previewName, URL.createObjectURL(file[0]));
                                } catch (e) {
                                    console.error("Error in signature upload:", e);
                                    setValue(name, undefined);
                                    setValue(previewName, undefined);
                                    toast({type: "error", title: "Error uploading image"});
                                }
                            }}
                        >
                            {({getRootProps, getInputProps}) => (
                                <>
                                    {getValues(previewName) ? (
                                        <div
                                            {...getRootProps({
                                                className: "dropzone",
                                                onDrop: (event) => event.stopPropagation(),
                                            })}
                                        >
                                            <input {...getInputProps()} />
                                            <Container className={styles.changeImageContainer}>
                                                <TypoGraph
                                                    variant={"h3"}
                                                    content={"Change Image"}
                                                    align="center"
                                                    color={colors.primary}
                                                    className={styles.changeImageBtn}
                                                />
                                            </Container>
                                        </div>
                                    ) : (
                                        <div
                                            {...getRootProps({
                                                className: "dropzone",
                                                onDrop: (event) => event.stopPropagation(),
                                            })}
                                            className={styles.dropzoneContainer}
                                        >
                                            <input {...getInputProps()} />
                                            <Container className={styles.fileUploadText}>
                                                <Icon name="icon_camera" size={46}/>
                                                <TypoGraph
                                                    content={label}
                                                    color={colors.primary}
                                                    className={styles.dropzoneText}
                                                />
                                            </Container>
                                        </div>
                                    )}
                                </>
                            )}
                        </Dropzone>
                        <ErrorMessage errors={errors} name={name}/>
                    </Container>
                );
            }}
        />
    );
};

export default SignatureDropzoneController;

